import $ from "jquery"

const PLAYBACK_SPEED_MAX = 2
const PLAYBACK_SPEED_STEP = 0.5
export const IGNORE_CHILDREN_ATTR = "data-ignore-children"

interface CallEventMarker {
  startTime: number
  endTime: number | null
  isPointInTimeEvent: boolean
  elem: HTMLElement
}

class AudioPlayer extends HTMLElement {
  // Attributes must be lowercase here.
  static observedAttributes = ["src", "startat", "forceattrchange", "forceplay"]

  protected initialized: boolean = false
  protected playbackSpeed: number = 1
  protected callEventMarkers: CallEventMarker[]
  protected audio: HTMLMediaElement
  protected duration: HTMLElement
  protected progress: HTMLElement

  attributeChangedCallback(
    name: string,
    oldValue: string,
    newValue: string,
  ): void {
    if (!this.initialized || oldValue === newValue) {
      return
    }

    if (name === "src" || name === "startat") {
      this.addContent()
    } else if (name === "forceattrchange") {
      // Content within this audio-player tag may have changed; recreate the
      // objections that come from that content. We need to wait for the next
      // event loop iteration because the attribute change can come slightly
      // before the content change.
      setTimeout(() => {
        for (const callEventMarker of this.callEventMarkers) {
          callEventMarker.elem.remove()
        }
        this.addCallEventMarkers()
        // To reposition the bars.
        this.updateDuration()
      }, 0)
    } else if (name === "forceplay" && Number(newValue) > 0) {
      // When audio is triggered with forcePlay it will restart from it's
      // original startAt position and begin playing.
      const startAt = this.getAttribute("startAt")
      if (startAt !== null && this.hasAttribute("forceplay")) {
        this.audio.currentTime = Number(startAt)
        setTimeout(() => this.audio.play(), 0)
      }
    }
  }

  connectedCallback(): void {
    if (this.initialized) {
      return
    }
    this.initialized = true
    this.addContent()
  }

  addContent(): void {
    const ignoreChildrenDiv = this.querySelector(`div[${IGNORE_CHILDREN_ATTR}]`)
    if (!ignoreChildrenDiv) {
      throw new Error(
        "Expected div with ignoreChildren attribute to house contents",
      )
    }
    ignoreChildrenDiv.innerHTML = ""

    const container = document.createElement("div")
    container.classList.add("audio", "fr", "aic")
    ignoreChildrenDiv.appendChild(container)

    this.audio = document.createElement("audio")
    let src = this.getAttribute("src")
    if (!src) {
      throw new Error("Missing src attribute")
    }
    const startAt = this.getAttribute("startAt")
    if (startAt) {
      src += `#t=${Number(startAt)}`
    }

    this.audio.setAttribute("src", src)
    this.audio.setAttribute("preload", "metadata")
    if (this.hasAttribute("autoplay")) {
      this.audio.setAttribute("autoplay", "true")
    }
    container.appendChild(this.audio)

    const heapPage = this.getAttribute("heapPage")

    const playButton = document.createElement("button")
    playButton.classList.add("play")
    if (heapPage) {
      playButton.classList.add(`${heapPage}-audio-play-button`)
    }
    // Default button type is submit which causes unwanted refreshes
    playButton.type = "button"
    container.appendChild(playButton)

    const speedButton = document.createElement("button")
    speedButton.classList.add("speed", "text-small", "mr0")
    if (heapPage) {
      speedButton.classList.add(`${heapPage}-audio-speed-button`)
    }
    speedButton.type = "button"
    container.appendChild(speedButton)
    this.setPlaybackSpeed(speedButton)

    const time = document.createElement("div")
    time.classList.add("time", "mr1")
    container.appendChild(time)

    this.progress = document.createElement("div")
    this.progress.classList.add("progress", "f1", "fr", "aic", "mr1")
    if (heapPage) {
      this.progress.classList.add(`${heapPage}-audio-progress-bar`)
    }
    container.appendChild(this.progress)

    const barBefore = document.createElement("div")
    barBefore.classList.add("bar", "blue")
    this.progress.appendChild(barBefore)

    const knob = document.createElement("div")
    knob.classList.add("knob")
    this.progress.appendChild(knob)

    const barAfter = document.createElement("div")
    barAfter.classList.add("bar", "white", "f1")
    this.progress.appendChild(barAfter)

    this.addCallEventMarkers()

    this.duration = document.createElement("div")
    this.duration.classList.add("duration")
    container.appendChild(this.duration)

    playButton.addEventListener("click", () =>
      this.audio.paused ? this.audio.play() : this.audio.pause(),
    )
    speedButton.addEventListener("click", () => {
      this.changePlaybackSpeed()
      this.setPlaybackSpeed(speedButton)
    })
    this.audio.addEventListener("play", () =>
      playButton.classList.add("playing"),
    )
    this.audio.addEventListener("pause", () => {
      playButton.classList.remove("playing")
    })

    this.updateTime(time, barBefore)
    this.updateDuration()
    this.audio.addEventListener("timeupdate", () =>
      this.updateTime(time, barBefore),
    )
    this.audio.addEventListener("durationchange", () => this.updateDuration())

    this.progress.addEventListener("click", event => {
      // Don't adjust audio if user clicks tippy tooltip action.
      const { target } = event
      if (
        target instanceof HTMLElement &&
        target.classList.contains("tooltip-action")
      ) {
        return
      }

      this.setTime(event)
    })
    this.progress.addEventListener("mousemove", event => {
      if (event.buttons === 1) {
        this.setTime(event)
        if (!this.audio.paused) {
          audiosToStart.push(this.audio)
          this.audio.pause()
        }
      }
    })
  }

  getElementWithToolTip(callEventElement: Element): HTMLDivElement {
    const origTippy = callEventElement.querySelector("tippy-tooltip")
    if (!origTippy) {
      throw new Error("Couldn't find tippy-tooltip")
    }

    const tippy = origTippy.cloneNode(true) as HTMLElement
    const elem = document.createElement("div")
    elem.style.position = "absolute"
    elem.appendChild(tippy)

    return elem
  }

  addCallEventMarkers(): void {
    // Only query immediate children to not get confused by contents within
    // ignoreChildrenDiv.
    const callEventMarkerElements = this.querySelectorAll(
      ":scope > div[data-start-time]",
    )
    this.callEventMarkers = Array.from(callEventMarkerElements).map(
      callEvent => {
        const isPointInTimeEvent = callEvent.hasAttribute(
          "data-point-in-time-event",
        )
        const startTimeAttr = callEvent.getAttribute("data-start-time")
        const startTime = Number(startTimeAttr)
        if (isNaN(startTime)) {
          throw new Error(`Invalid startTime ${startTimeAttr}`)
        }

        const endTimeAttr = callEvent.getAttribute("data-end-time")
        const endTime = endTimeAttr ? Number(endTimeAttr) : null
        if (endTime !== null && isNaN(endTime)) {
          throw new Error(`Invalid endTime ${endTimeAttr}`)
        }

        const elem = this.getElementWithToolTip(callEvent)
        return {
          startTime,
          endTime,
          elem,
          isPointInTimeEvent,
        }
      },
    )

    for (const callEventMarker of this.callEventMarkers) {
      this.progress.appendChild(callEventMarker.elem)
    }
  }

  setPlaybackSpeed(speedButton: HTMLButtonElement): void {
    speedButton.textContent = `${this.playbackSpeed}x`
    this.audio.playbackRate = this.playbackSpeed
  }

  changePlaybackSpeed(): void {
    if (this.playbackSpeed >= PLAYBACK_SPEED_MAX) {
      this.playbackSpeed = 1
    } else {
      this.playbackSpeed += PLAYBACK_SPEED_STEP
    }
  }

  isValidDuration(): boolean {
    return isFinite(this.audio.duration) && this.audio.duration > 0
  }

  updateTime(time: HTMLElement, barBefore: HTMLElement): void {
    time.textContent = `${this.audio.currentTime.toFixed(0)}s`
    if (this.isValidDuration()) {
      barBefore.style.width = `${
        (this.audio.currentTime / this.audio.duration) * 100
      }%`
    }
  }

  updateDuration(): void {
    if (this.isValidDuration()) {
      this.duration.textContent = `${this.audio.duration.toFixed(0)}s`
      for (const callEventMarker of this.callEventMarkers) {
        callEventMarker.elem.style.left = `${(callEventMarker.startTime / this.audio.duration) * 100}%`

        if (!callEventMarker.isPointInTimeEvent) {
          const objectionDuration =
            (callEventMarker.endTime ?? this.audio.duration) -
            callEventMarker.startTime
          callEventMarker.elem.style.width = `${
            (objectionDuration / this.audio.duration) * 100
          }%`
        }
      }
    }
  }

  setTime(event: MouseEvent): void {
    const $progress = $(this.progress)
    const offsetX =
      event.pageX - ($progress.offset() as JQuery.Coordinates).left
    this.audio.currentTime =
      (offsetX / ($progress.width() as number)) * this.audio.duration
  }
}

customElements.define("audio-player", AudioPlayer)

let audiosToStart: HTMLMediaElement[] = []
window.addEventListener("mouseup", () => {
  audiosToStart.forEach(async audio => await audio.play())
  audiosToStart = []
})
